/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const NGOReview: React.FC = () => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ngo, setNgo] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (ngoId) {
      loadNGO();
      loadDocuments();
    }
  }, [ngoId]);

  const loadNGO = async () => {
    if (!ngoId) return;

    const { data, error } = await supabase
      .from('ngos')
      .select('*')
      .eq('id', ngoId)
      .single();

    if (!error && data) {
      setNgo(data);
    }
  };

  const loadDocuments = async () => {
    if (!ngoId) return;

    const { data, error } = await supabase
      .from('ngo_documents')
      .select('*')
      .eq('ngo_id', ngoId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setDocuments(data);
    }
  };

  const handleVerify = async (status: 'verified' | 'rejected') => {
    if (!ngoId) return;
    if (status === 'rejected' && !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (status === 'rejected') {
        updateData.rejection_reason = rejectionReason;
      }

      const { error } = await supabase
        .from('ngos')
        .update(updateData)
        .eq('id', ngoId);

      if (error) throw error;

      // If verified, mark documents as verified
      if (status === 'verified') {
        await supabase
          .from('ngo_documents')
          .update({
            is_verified: true,
            verified_at: new Date().toISOString(),
            verified_by: user.id
          })
          .eq('ngo_id', ngoId);
      }

      navigate(`/client/company/ngos`);
    } catch (err: any) {
      alert(err.message || 'Failed to update NGO status');
    } finally {
      setLoading(false);
    }
  };

  if (!ngo) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>Review NGO Profile</h1>
          <span className={`status-badge status-${ngo.status}`}>{ngo.status}</span>
        </div>

        <div className="card">
          <h2>Basic Information</h2>
          <div className="form-row">
            <div>
              <strong>Legal Name:</strong>
              <p>{ngo.legal_name}</p>
            </div>
            <div>
              <strong>Registration Type:</strong>
              <p>{ngo.registration_type}</p>
            </div>
          </div>
          {ngo.pan && (
            <div>
              <strong>PAN:</strong>
              <p>{ngo.pan}</p>
            </div>
          )}
        </div>

        <div className="card">
          <h2>Address</h2>
          <p>
            {ngo.address?.street}<br />
            {ngo.address?.city}, {ngo.address?.state} {ngo.address?.pincode}<br />
            {ngo.address?.country}
          </p>
        </div>

        <div className="card">
          <h2>Contact Information</h2>
          <div className="form-row">
            <div>
              <strong>Name:</strong>
              <p>{ngo.contact?.name}</p>
            </div>
            <div>
              <strong>Email:</strong>
              <p>{ngo.contact?.email}</p>
            </div>
            <div>
              <strong>Phone:</strong>
              <p>{ngo.contact?.phone}</p>
            </div>
          </div>
        </div>

        {ngo.bank_details && (
          <div className="card">
            <h2>Bank Details</h2>
            <div className="form-row">
              <div>
                <strong>Account Number:</strong>
                <p>{ngo.bank_details?.account_number || 'N/A'}</p>
              </div>
              <div>
                <strong>IFSC:</strong>
                <p>{ngo.bank_details?.ifsc || 'N/A'}</p>
              </div>
              <div>
                <strong>Bank Name:</strong>
                <p>{ngo.bank_details?.bank_name || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <h2>Documents</h2>
          {documents.length === 0 ? (
            <p style={{ color: 'var(--muted)' }}>No documents uploaded</p>
          ) : (
            <div className="document-list">
              {documents.map((doc) => (
                <div key={doc.id} className="document-item">
                  <div className="document-item-info">
                    <span>📄</span>
                    <div>
                      <strong>{doc.document_type?.toUpperCase() || 'DOCUMENT'}</strong>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                        {doc.file_name}
                      </p>
                      {doc.is_verified && (
                        <span className="status-badge status-verified" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline">
                    View
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        {ngo.status === 'submitted' && (
          <div className="card">
            <h2>Verification Action</h2>

            <div className="form-group">
              <label>Rejection Reason (if rejecting)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Provide reason for rejection..."
                rows={3}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid var(--border)' }}
              />
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleVerify('rejected')}
                disabled={loading}
              >
                Reject
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleVerify('verified')}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Approve & Verify'}
              </button>
            </div>
          </div>
        )}

        {ngo.rejection_reason && (
          <div className="card" style={{ background: '#fee2e2', border: '1px solid #ef4444' }}>
            <h3>Rejection Reason</h3>
            <p>{ngo.rejection_reason}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NGOReview;


